import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useReadinessReport } from '@/hooks/useAnalyticsReports';
import { config } from '@/config/config';
import ReportFilters from './components/filters/ReportFilters';
import type { ReportFiltersState } from './components/filters/ReportFilters';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';

const COLORS = config.reports.display.chartColors;

interface ReadinessReportProps {
  filters?: ReportFiltersState;
}

const ReadinessReport: React.FC<ReadinessReportProps> = ({ filters = {} }) => {
  const [localFilters, setLocalFilters] = useState<ReportFiltersState>(filters);
  const { data, isLoading, error } = useReadinessReport();

  // Get translations
  const i18n = useReportspageTranslations();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertDescription>
          {i18n.errors.readiness.failedToLoad(
            error instanceof Error ? error.message : i18n.errors.readiness.unknownError
          )}
        </AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert>
        <AlertCircle className="size-4" />
        <AlertDescription>{i18n.errors.readiness.noDataAvailable}</AlertDescription>
      </Alert>
    );
  }

  const readinessPercentage = data.readiness_percentage || 0;
  const totalAttributes = data.total_attributes || 0;
  const completedAttributes = data.completed_attributes || 0;
  const missingAttributes = totalAttributes - completedAttributes;

  // Prepare chart data
  const chartData = [
    {
      name: i18n.chartDataLabels.readiness.completed,
      value: completedAttributes,
      color: COLORS[0],
    },
    {
      name: i18n.chartDataLabels.readiness.missing,
      value: missingAttributes,
      color: COLORS[1],
    },
  ];

  // Channel breakdown data
  const channelData = data.channel_breakdown || [];

  // Field breakdown data
  const fieldData = data.field_breakdown || [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <ReportFilters
        filters={localFilters}
        onFiltersChange={setLocalFilters}
        availableFilters={['locale', 'channel', 'family']}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {i18n.summaryCards.readiness.overallReadiness}
                </p>
                <p className="text-2xl font-bold">{readinessPercentage.toFixed(1)}%</p>
              </div>
              <div className="flex items-center">
                {readinessPercentage >= 80 ? (
                  <TrendingUp className="size-5 text-green-500" />
                ) : readinessPercentage >= 60 ? (
                  <Minus className="size-5 text-yellow-500" />
                ) : (
                  <TrendingDown className="size-5 text-red-500" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {i18n.summaryCards.readiness.totalAttributes}
              </p>
              <p className="text-2xl font-bold">{totalAttributes.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {i18n.summaryCards.readiness.completed}
              </p>
              <p className="text-2xl font-bold text-green-600">
                {completedAttributes.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {i18n.summaryCards.readiness.missing}
              </p>
              <p className="text-2xl font-bold text-red-600">
                {missingAttributes.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Completion Overview */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.charts.readiness.completionOverview}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={value => [value.toLocaleString(), i18n.charts.readiness.attributes]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Channel Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.charts.readiness.channelReadiness}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {channelData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={channelData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} unit="%" />
                    <Tooltip formatter={value => [`${value}%`, i18n.charts.readiness.readiness]} />
                    <Bar dataKey="value" fill={COLORS[0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-gray-500">
                  <div className="text-center">
                    <p>{i18n.errors.readiness.noChannelData}</p>
                    <p className="text-sm">{i18n.errors.readiness.channel(data.channel)}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Field Breakdown */}
      {fieldData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{i18n.charts.readiness.fieldBreakdown}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fieldData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip />
                  <Bar
                    dataKey="completed"
                    stackId="a"
                    fill={COLORS[0]}
                    name={i18n.chartDataLabels.readiness.completed}
                  />
                  <Bar
                    dataKey="missing"
                    stackId="a"
                    fill={COLORS[1]}
                    name={i18n.chartDataLabels.readiness.missing}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReadinessReport;
